import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import logo from '../images/landp.png'; // Assuming this is the path to your logo
import { FaHeart, FaShoppingBag } from 'react-icons/fa';
import { MdMenu, MdClose } from 'react-icons/md';

// Animation keyframes
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -100% 0; }
  100% { background-position: 200% 0; }
`;

// Styled components
const NavbarContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${props => props.scrolled ? 'rgba(255, 255, 255, 0.95)' : 'rgba(246, 240, 247, 0.95)'};
  padding: ${props => props.scrolled ? '0.7rem 2rem' : '1rem 2rem'};
  box-shadow: ${props => props.scrolled ? '0 4px 15px rgba(122, 73, 136, 0.15)' : 'none'};
  backdrop-filter: blur(5px);
  transition: all 0.3s ease;
  z-index: 1000;

  &::before {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: linear-gradient(90deg, #FFAAD7, #B67EEB, #FFAAD7);
    background-size: 200% 100%;
    animation: ${shimmer} 6s infinite linear;
    opacity: ${props => props.scrolled ? '1' : '0'};
    transition: opacity 0.3s ease;
  }

  @media (max-width: 768px) {
    padding: ${props => props.scrolled ? '0.5rem 1rem' : '0.8rem 1rem'};
  }
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  z-index: 100;
`;

const LogoImage = styled.img`
  height: 45px;
  width: auto;
  margin-right: 10px;
  transition: transform 0.3s ease;

  ${LogoContainer}:hover & {
    transform: scale(1.05);
  }
`;

const LogoText = styled.div`
  font-family: 'Dancing Script', cursive;
  font-size: 1.5rem;
  font-weight: bold;
  color: #7A4988;
  transition: color 0.3s ease;
  display: flex;
  align-items: center;

  ${LogoContainer}:hover & {
    color: #FF85C0;
  }

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const HeartIcon = styled(FaHeart)`
  color: #FF85C0;
  margin-left: 5px;
  font-size: 0.8rem;
`;

const NavlinksContainer = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    position: fixed;
    flex-direction: column;
    justify-content: center;
    top: 0;
    right: ${props => props.isOpen ? '0' : '-100%'};
    width: 75%;
    height: 100vh;
    background: linear-gradient(135deg, #FFCDE7 0%, #F3E3F7 100%);
    transition: right 0.3s ease-in-out;
    box-shadow: ${props => props.isOpen ? '-5px 0 20px rgba(0, 0, 0, 0.2)' : 'none'};
    z-index: 99;
  }
`;

const NavLink = styled(Link)`
  margin: 0 1.2rem;
  padding: 0.5rem 0;
  text-decoration: none;
  color: #7A4988;
  font-weight: 500;
  font-size: 1rem;
  position: relative;
  transition: all 0.3s ease;

  &::after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: 0;
    left: 0;
    background-color: #FF85C0;
    transition: width 0.3s ease;
  }

  &:hover {
    color: #FF85C0;
    transform: translateY(-2px);

    &::after {
      width: 100%;
    }
  }

  @media (max-width: 768px) {
    margin: 1.5rem 0;
    font-size: 1.2rem;
  }
`;

const ShopButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #FFAAD7;
  color: white;
  padding: 0.6rem 1.2rem;
  border-radius: 25px;
  margin-left: 1rem;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 3px 8px rgba(255, 170, 215, 0.4);

  &:hover {
    background-color: #FF85C0;
    transform: translateY(-3px);
    box-shadow: 0 5px 12px rgba(255, 170, 215, 0.5);
  }

  @media (max-width: 768px) {
    margin: 1.5rem 0 0;
    padding: 0.7rem 1.4rem;
  }
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.8rem;
  color: #7A4988;
  cursor: pointer;
  z-index: 100;

  @media (max-width: 768px) {
    display: block;
  }
`;

const CloseOverlay = styled.div`
  display: ${props => props.isOpen ? 'block' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 98;
`;

function Navigation() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Handle scrolling effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 30) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu when window is resized to desktop size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && menuOpen) {
        setMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [menuOpen]);

  // Prevent body scrolling when mobile menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [menuOpen]);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  return (
    <>
      <NavbarContainer scrolled={scrolled}>
        <LogoContainer to="/" onClick={closeMenu}>
          <LogoImage src={logo} alt="Lavender & Palm Co." />
          <LogoText>
            Welcome to Lavender and Palm<HeartIcon />
          </LogoText>
        </LogoContainer>

        <MenuButton onClick={toggleMenu}>
          {menuOpen ? <MdClose /> : <MdMenu />}
        </MenuButton>

        <NavlinksContainer isOpen={menuOpen}>
          <NavLink to="/" onClick={closeMenu}>Home</NavLink>
          <NavLink to="/custom-order" onClick={closeMenu}>Custom Orders</NavLink>
          <NavLink to="/gallery" onClick={closeMenu}>Gallery</NavLink>
          <ShopButton to="/ready-to-ship" onClick={closeMenu}>
            <FaShoppingBag /> Shop Now
          </ShopButton>
        </NavlinksContainer>
      </NavbarContainer>

      <CloseOverlay isOpen={menuOpen} onClick={closeMenu} />
    </>
  );
}

export default Navigation;